import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useCallback,
} from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useLocation } from 'react-router-dom';
import { useOverviewContext } from './OverviewContext';
import { useSelectedStudent } from './SelectedStudentContext';
import {
  normalizeTasklistJsonPayload,
  type TasklistLearningCenterTask,
  type TasklistHeroTask,
} from '@/types/tasklistJson';
import {
  fetchTasksAutocomplete,
  parseTaskCompletionRowsFromTasksPayload,
} from '@/services/taskCompletionApiService';
import { readTasksSnapshotJson } from '@/utils/tasksSnapshotSession';
import {
  mergeLearningCenterTasksWithApi,
  resolveLearningCenterBackendTaskId,
  type TaskCompletionApiRow,
} from '@/utils/learningCenterTasksMerge';
import { studentPreviewRole, shouldSkipStudentScopedBackendApi } from '@/utils/toolSettingsUtils';

export interface TasklistDataRow {
  checklistId: number;
  checklistName: string;
  description: string;
  roles?: string[];
  pageId?: string;
  /** Preferred `/task/page/{slug}` segment when set (matches `tasklist.json` hero `pathSegment`). */
  pathSegment?: string;
  group?: string;
  /** CMS parent id for grouping subtasks from GET `/tasks` when it differs from static `pageId` (hero tasks only). */
  subtasksCmsParentId?: number;
}

export interface TasklistTopicStatusRow {
  accountId: number;
  checklistId: number;
  status: string;
}

export interface HeroTasklistTask {
  id: number;
  title: string;
  checklistName: string;
  completed: boolean;
}

/** Learning Center rows: structure from `tasklist.json`; `completed` from API merge in context. */
export interface LearningCenterTaskRow {
  id: number;
  title: string;
  description: string;
  taskType: 'anytime' | 'actionable';
  completed: boolean;
  order: number;
  pageId?: string;
  pathSegment?: string;
  group?: string;
  roles?: string[];
  /** Backend task id from tasklist.json when present */
  taskId?: number;
}

interface TasklistContextType {
  tasklistData: TasklistDataRow[];
  tasklistTopicStatus: TasklistTopicStatusRow[];
  heroTasks: HeroTasklistTask[];
  learningCenterTasks: LearningCenterTaskRow[];
  markTasklistTopicRead: (
    accountId: number,
    checklistId: number,
    status: string
  ) => Promise<void>;
  /** After LC detail page updates backend, sync merged list for Overview / nav. */
  patchLearningCenterTaskCompletion: (taskId: number, completed: boolean) => void;
  /** Re-fetch task completion and re-merge with tasklist.json (home / Overview list). */
  refreshLearningCenterTaskCompletionFromApi: () => Promise<void>;
}

const TasklistContext = createContext<TasklistContextType | undefined>(undefined);

const TASKLIST_JSON_URL = '/data/tasklist.json';

function taskVisibleForRole(task: TasklistHeroTask, userRoleVal: string | undefined): boolean {
  if (!task.roles?.length) return true;
  return userRoleVal ? task.roles.includes(userRoleVal) : false;
}

function mapTasksToTasklistData(tasks: TasklistHeroTask[]): TasklistDataRow[] {
  return tasks.map((t) => ({
    checklistId: t.id,
    checklistName: t.checklistName,
    description: t.title,
    roles: t.roles,
    pageId: t.pageId?.trim() || undefined,
    pathSegment: t.pathSegment?.trim() || undefined,
    group: t.group,
    subtasksCmsParentId:
      t.subtasksCmsParentId != null && Number.isFinite(Number(t.subtasksCmsParentId))
        ? Number(t.subtasksCmsParentId)
        : undefined,
  }));
}

function buildInitialTopicStatus(
  tasks: TasklistHeroTask[],
  students: { accountId: number }[]
): TasklistTopicStatusRow[] {
  const rows: TasklistTopicStatusRow[] = [];
  const studentList = students?.length ? students : [];

  for (const t of tasks) {
    const checklistId = Number(t.id);
    const completed = Boolean(t.completed);
    const statusStr = completed ? 'true' : 'false';

    if (t.accountId != null && t.accountId !== '') {
      rows.push({
        accountId: Number(t.accountId),
        checklistId,
        status: statusStr,
      });
      continue;
    }

    if (!studentList.length) continue;

    for (const s of studentList) {
      rows.push({
        accountId: s.accountId,
        checklistId,
        status: statusStr,
      });
    }
  }

  return rows;
}

export const TasklistProvider = ({ children }: { children: ReactNode }) => {
  const { authState } = useOktaAuth();
  const { students, userRoleVal } = useOverviewContext();
  const { selectedStudent } = useSelectedStudent();
  const location = useLocation();

  const accessToken = authState?.accessToken?.accessToken;

  const [tasklistData, setTasklistData] = useState<TasklistDataRow[]>([]);
  const [tasklistTopicStatus, setTasklistTopicStatus] = useState<TasklistTopicStatusRow[]>([]);
  const [learningCenterTasks, setLearningCenterTasks] = useState<LearningCenterTaskRow[]>([]);
  const [filteredJsonTasks, setFilteredJsonTasks] = useState<TasklistHeroTask[]>([]);
  const [learningCenterJsonTasks, setLearningCenterJsonTasks] = useState<TasklistLearningCenterTask[]>([]);
  const [lcApiCompletion, setLcApiCompletion] = useState<TaskCompletionApiRow[] | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetch(TASKLIST_JSON_URL)
      .then((r) => {
        if (!r.ok) throw new Error('tasklist.json');
        return r.json();
      })
      .then((data: unknown) => {
        if (cancelled) return;
        const { tasks: rawTasks, learningCenterTasks: lc } = normalizeTasklistJsonPayload(data);
        const previewRole = studentPreviewRole(userRoleVal);
        const visible = rawTasks.filter((t) => taskVisibleForRole(t, previewRole));
        setFilteredJsonTasks(visible);
        setTasklistData(mapTasksToTasklistData(visible));

        setLearningCenterJsonTasks(lc);
      })
      .catch((err) => {
        console.error('Local tasklist.json load error:', err);
        if (!cancelled) {
          setFilteredJsonTasks([]);
          setTasklistData([]);
          setLearningCenterJsonTasks([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [userRoleVal]);

  useEffect(() => {
    if (!learningCenterJsonTasks.length) {
      setLearningCenterTasks([]);
      return;
    }
    if (lcApiCompletion === null) {
      setLearningCenterTasks(
        learningCenterJsonTasks.map((t) => ({
          id: t.id,
          taskId: t.taskId,
          title: t.title,
          description: t.description,
          taskType: t.taskType,
          completed: Boolean(t.completed),
          order: t.order,
          pageId: t.pageId,
          pathSegment: t.pathSegment,
          group: t.group,
          roles: t.roles,
        }))
      );
      return;
    }
    setLearningCenterTasks(mergeLearningCenterTasksWithApi(learningCenterJsonTasks, lcApiCompletion));
  }, [learningCenterJsonTasks, lcApiCompletion]);

  useEffect(() => {
    if (!filteredJsonTasks.length || !students?.length) {
      setTasklistTopicStatus([]);
      return;
    }
    setTasklistTopicStatus(buildInitialTopicStatus(filteredJsonTasks, students));
  }, [filteredJsonTasks, students]);

  useEffect(() => {
    let cancelled = false;
    const identityId = selectedStudent?.identityId;

    if (!accessToken || !identityId || shouldSkipStudentScopedBackendApi(identityId, userRoleVal)) {
      setLcApiCompletion(null);
      return;
    }

    /** Student picker only — no checklist/LC surfaces need GET `/tasks` merge here. */
    if (location.pathname === '/learning-coach') {
      setLcApiCompletion(null);
      return () => {
        cancelled = true;
      };
    }

    /** Prefer session snapshot (Overview / Mark Complete / prior GET) — avoids GET `/tasks` on navigation. */
    const cachedSnapshot = readTasksSnapshotJson(identityId);
    if (cachedSnapshot != null) {
      if (!cancelled) {
        setLcApiCompletion(parseTaskCompletionRowsFromTasksPayload(cachedSnapshot));
      }
      return () => {
        cancelled = true;
      };
    }

    fetchTasksAutocomplete(accessToken, identityId)
      .then((rows) => {
        if (!cancelled) setLcApiCompletion(rows);
      })
      .catch((err) => {
        console.error('Learning Center task completion API error:', err);
        if (!cancelled) setLcApiCompletion(null);
      });

    return () => {
      cancelled = true;
    };
  }, [accessToken, selectedStudent?.identityId, location.pathname, userRoleVal]);

  const heroTasks = useMemo((): HeroTasklistTask[] => {
    if (!selectedStudent) return [];

    const studentRows = tasklistTopicStatus.filter(
      (s) => String(s.accountId) === String(selectedStudent.accountId)
    );

    return tasklistData.map((item) => {
      const status = studentRows.find((s) => Number(s.checklistId) === Number(item.checklistId));
      const fromTopic = status?.status === 'true';

      let completed = fromTopic;
      const jsonTask = filteredJsonTasks.find((t) => Number(t.id) === Number(item.checklistId));
      if (lcApiCompletion !== null && jsonTask) {
        const backendId = resolveLearningCenterBackendTaskId(jsonTask);
        const apiRow = lcApiCompletion.find((r) => Number(r.taskId) === Number(backendId));
        if (apiRow !== undefined) {
          completed = Boolean(apiRow.completed);
        }
      }

      return {
        id: item.checklistId,
        checklistName: item.checklistName || '',
        title: item.description || 'Untitled Task',
        completed,
      };
    });
  }, [tasklistData, tasklistTopicStatus, selectedStudent, lcApiCompletion, filteredJsonTasks]);

  const refreshLearningCenterTaskCompletionFromApi = useCallback(async () => {
    const token = authState?.accessToken?.accessToken;
    const identityId = selectedStudent?.identityId;
    if (!token || !identityId || shouldSkipStudentScopedBackendApi(identityId, userRoleVal)) return;
    try {
      /** Avoid a redundant GET right after `fetchTasksSnapshotAndPersist` (same payload is already in session). */
      const fromSession = readTasksSnapshotJson(identityId);
      if (fromSession != null) {
        setLcApiCompletion(parseTaskCompletionRowsFromTasksPayload(fromSession));
        return;
      }
      const rows = await fetchTasksAutocomplete(token, identityId);
      setLcApiCompletion(rows);
    } catch (err) {
      console.error('Learning Center task completion refresh failed:', err);
    }
  }, [authState?.accessToken?.accessToken, selectedStudent?.identityId, userRoleVal]);

  const patchLearningCenterTaskCompletion = useCallback((taskId: number, completed: boolean) => {
    setLearningCenterTasks((prev) =>
      prev.map((t) => (Number(t.id) === Number(taskId) ? { ...t, completed } : t))
    );
    setLcApiCompletion((prev) => {
      if (prev === null) return prev;
      const next = [...prev];
      const idx = next.findIndex((r) => Number(r.taskId) === Number(taskId));
      if (idx >= 0) next[idx] = { ...next[idx], completed };
      else next.push({ taskId: Number(taskId), completed });
      return next;
    });
  }, []);

  const markTasklistTopicRead = useCallback(
    async (accountId: number, checklistId: number, status: string) => {
      setTasklistTopicStatus((prev) => {
        const next = [...prev];
        const idx = next.findIndex(
          (r) =>
            Number(r.accountId) === Number(accountId) &&
            Number(r.checklistId) === Number(checklistId)
        );
        if (idx >= 0) {
          next[idx] = { ...next[idx], status };
        } else {
          next.push({ accountId, checklistId, status });
        }
        return next;
      });
    },
    []
  );

  const value = useMemo(
    () => ({
      tasklistData,
      tasklistTopicStatus,
      heroTasks,
      learningCenterTasks,
      markTasklistTopicRead,
      patchLearningCenterTaskCompletion,
      refreshLearningCenterTaskCompletionFromApi,
    }),
    [
      tasklistData,
      tasklistTopicStatus,
      heroTasks,
      learningCenterTasks,
      markTasklistTopicRead,
      patchLearningCenterTaskCompletion,
      refreshLearningCenterTaskCompletionFromApi,
    ]
  );

  return <TasklistContext.Provider value={value}>{children}</TasklistContext.Provider>;
};

export const useTasklist = () => {
  const context = useContext(TasklistContext);
  if (!context) {
    throw new Error('useTasklist must be used within a TasklistProvider');
  }
  return context;
};
